import React, { useState, useEffect } from 'react';
import { 
  Layout, 
  Typography, 
  Row, 
  Col, 
  Card, 
  Button,
  Space,
  Statistic,
  Progress,
  Alert,
  Divider,
  List,
  Avatar,
  Tag
} from 'antd';
import { 
  GiftOutlined, 
  ShareAltOutlined, 
  DollarOutlined, 
  UserOutlined, 
  TrophyOutlined,
  CheckCircleOutlined,
  StarOutlined,
  HeartOutlined
} from '@ant-design/icons';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { referralAnalyticsService } from '../../services/referralService';
import { ReferralDashboard } from '../../components/features/referral';
import { SecondaryButton } from '../../components/common/ui';
import { AuthModal } from '../../components/auth';
import { theme } from '../../styles/theme';
import { Header } from '../../components/common/layout';

const { Content } = Layout;
const { Title, Paragraph, Text } = Typography;

const ReferralProgramPage = () => {
  const { currentUser } = useAuth();
  const [referralStats, setReferralStats] = useState(null);
  const [topReferrers, setTopReferrers] = useState([]);
  const [authModalVisible, setAuthModalVisible] = useState(false);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    fetchReferralStats();
  }, []);

  const fetchReferralStats = async () => {
    try {
      const stats = await referralAnalyticsService.getReferralStats();
      setReferralStats(stats);
      
      const topReferrersData = await referralAnalyticsService.getTopReferrers(5);
      setTopReferrers(topReferrersData);
    } catch (error) {
      console.error('Error fetching referral stats:', error);
    }
  };

  if (currentUser) {
    return (
      <>
        <Header />
        <div style={{ marginTop: '80px' }}>
          <ReferralDashboard />
        </div>
      </>
    );
  }

  return (
    <>
      <Header />
      <Layout style={{ minHeight: '100vh', paddingTop: '80px' }}>
      {/* Hero Section */}
      <section style={{ 
        background: `linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.primaryDark} 100%)`,
        padding: '60px 0',
        color: 'white',
        marginBottom: '3rem'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 24px' }}>
          <div style={{ textAlign: 'center' }}>
            <Title level={1} style={{ color: 'white', marginBottom: '1rem' }}>
              <GiftOutlined style={{ color: 'white', marginRight: '1rem' }} />
              Referral Program
            </Title>
            <Title level={2} style={{ color: 'white', marginBottom: '1rem' }}>
              Earn $25 for Every Friend You Refer!
            </Title>
            <Paragraph style={{ 
              color: 'white', 
              fontSize: '1.3rem', 
              maxWidth: '600px', 
              margin: '0 auto 2rem',
              fontFamily: theme.fonts.primary
            }}>
              Share the love of professional home maintenance and earn rewards for every friend who joins our service.
            </Paragraph>
            <SecondaryButton onClick={() => setAuthModalVisible(true)}>
              Start Earning Today
            </SecondaryButton>
          </div>
        </div>
      </section>

      <Content style={{ padding: '24px' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>


          {/* How It Works */}
          <Card style={{ 
            marginBottom: '3rem',
            borderRadius: theme.borderRadius.lg,
            boxShadow: theme.shadows.md,
            border: `1px solid ${theme.colors.borderGray}`
          }}>
            <Title level={3} style={{ 
              textAlign: 'center', 
              marginBottom: '2rem',
              color: theme.colors.primary,
              fontFamily: theme.fonts.primary
            }}>
              How It Works
            </Title>
            <Row gutter={[32, 32]}>
              <Col xs={24} sm={8}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ 
                    width: '80px', 
                    height: '80px', 
                    borderRadius: '50%', 
                    backgroundColor: theme.colors.primary, 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    margin: '0 auto 1rem',
                    fontSize: '2rem',
                    color: 'white'
                  }}>
                    1
                  </div>
                  <Title level={4} style={{ fontFamily: theme.fonts.primary, color: theme.colors.primary }}>
                    Sign Up
                  </Title>
                  <Paragraph style={{ fontFamily: theme.fonts.primary }}>
                    Create your account and get your unique referral code
                  </Paragraph>
                </div>
              </Col>
              <Col xs={24} sm={8}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ 
                    width: '80px', 
                    height: '80px', 
                    borderRadius: '50%', 
                    backgroundColor: theme.colors.success, 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    margin: '0 auto 1rem',
                    fontSize: '2rem',
                    color: 'white'
                  }}>
                    2
                  </div>
                  <Title level={4} style={{ fontFamily: theme.fonts.primary, color: theme.colors.primary }}>
                    Share
                  </Title>
                  <Paragraph style={{ fontFamily: theme.fonts.primary }}>
                    Share your referral code or link with friends and family
                  </Paragraph>
                </div>
              </Col>
              <Col xs={24} sm={8}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ 
                    width: '80px', 
                    height: '80px', 
                    borderRadius: '50%', 
                    backgroundColor: theme.colors.warning, 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    margin: '0 auto 1rem',
                    fontSize: '2rem',
                    color: 'white'
                  }}>
                    3
                  </div>
                  <Title level={4} style={{ fontFamily: theme.fonts.primary, color: theme.colors.primary }}>
                    Earn
                  </Title>
                  <Paragraph style={{ fontFamily: theme.fonts.primary }}>
                    Earn $25 credit when they complete their first service
                  </Paragraph>
                </div>
              </Col>
            </Row>
          </Card>

          {/* Benefits */}
          <Row gutter={[16, 16]} style={{ marginBottom: '3rem' }}>
            <Col xs={24} lg={12}>
              <Card>
                <Title level={4}>
                  <GiftOutlined style={{ color: '#52c41a', marginRight: '0.5rem' }} />
                  For You
                </Title>
                <List
                  dataSource={[
                    'Earn $25 credit for each successful referral',
                    'Credits can be used for any service',
                    'No limit on how many friends you can refer',
                    'Track your earnings in real-time',
                    'Get notified when you earn rewards'
                  ]}
                  renderItem={item => (
                    <List.Item>
                      <CheckCircleOutlined style={{ color: '#52c41a', marginRight: '0.5rem' }} />
                      {item}
                    </List.Item>
                  )}
                />
              </Card>
            </Col>
            <Col xs={24} lg={12}>
              <Card>
                <Title level={4}>
                  <HeartOutlined style={{ color: '#ff4d4f', marginRight: '0.5rem' }} />
                  For Your Friends
                </Title>
                <List
                  dataSource={[
                    'Get $25 off their first service',
                    'Access to professional home maintenance',
                    'Flexible membership plans',
                    'Expert workshops and training',
                    '24/7 customer support'
                  ]}
                  renderItem={item => (
                    <List.Item>
                      <CheckCircleOutlined style={{ color: '#52c41a', marginRight: '0.5rem' }} />
                      {item}
                    </List.Item>
                  )}
                />
              </Card>
            </Col>
          </Row>
          {/* Call to Action */}
          <Card style={{ 
            textAlign: 'center',
            borderRadius: theme.borderRadius.lg,
            boxShadow: theme.shadows.md,
            border: `1px solid ${theme.colors.borderGray}`
          }}>
            <Title level={3} style={{ 
              color: theme.colors.primary,
              fontFamily: theme.fonts.primary
            }}>
              Ready to Start Earning?
            </Title>
            <Paragraph style={{ 
              fontSize: '1.1rem', 
              marginBottom: '2rem',
              fontFamily: theme.fonts.primary
            }}>
              Join our referral program and start earning rewards today!
            </Paragraph>
            <Space size="large">
              <Button 
                type="primary" 
                size="large"
                onClick={() => setAuthModalVisible(true)}
                style={{ 
                  background: `linear-gradient(135deg, ${theme.colors.success}, #73d13d)`,
                  border: 'none',
                  height: '50px',
                  padding: '0 30px',
                  borderRadius: theme.borderRadius.xl,
                  fontFamily: theme.fonts.primary,
                  fontWeight: '600',
                  color: 'white'
                }}
              >
                Sign Up Now
              </Button>
              <SecondaryButton to="/services">
                View Services
              </SecondaryButton>
            </Space>
          </Card>

          {/* Terms */}
          <Alert
            message="Referral Program Terms"
            description={
              <div>
                <Paragraph style={{ margin: 0 }}>
                  • Referral rewards are credited to your account after the referred customer completes their first paid service
                </Paragraph>
                <Paragraph style={{ margin: 0 }}>
                  • Rewards can be used for any HSP service
                </Paragraph>
                <Paragraph style={{ margin: 0 }}>
                  • Self-referrals and fraudulent activity will result in account termination
                </Paragraph>
                <Paragraph style={{ margin: 0 }}>
                  • Program terms may change with 30 days notice
                </Paragraph>
              </div>
            }
            type="info"
            showIcon
            style={{ marginTop: '2rem' }}
          />
        </div>
      </Content>
      
      <AuthModal 
        visible={authModalVisible} 
        onClose={() => setAuthModalVisible(false)}
        referralCode={searchParams.get('ref')}
      />
    </Layout>
    </>
  );
};

export default ReferralProgramPage;
